import type { AgentTemplateDTO } from '@harness/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, Check, Copy, X } from 'lucide-react';
import TopBar from '../components/TopBar';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatTokens, MODE_ROLES, slugify, type AgentRuntime, type ConnectionMode, type ProjectDTO, type ServerConnectionDTO } from '@harness/shared';
import { Check2, GithubIcon, Spinner } from '../components/ui';
import { api, ApiError, errorText } from '../lib/api';
import { qk } from '../lib/keys';
import { useEnvironment, useIntegrations, useModels, useServer } from '../lib/queries';
import { toast } from '../lib/store';
import { GithubConnect, jiraBody, jiraDraft, JiraFields, jiraReady, useGithubRepos, type JiraDraft } from './settings/IntegrationsPage';
import { MfaRequired, MODES } from './settings/ServerPage';

const STEPS = ['Name', 'Server', 'GitHub & Jira', 'Model', 'Agents'];

interface RepoPick {
  on: boolean;
  owner: string;
  repo: string;
  path: string;
  base: string;
  role: string;
}

export default function Wizard() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [project, setProject] = useState<ProjectDTO | null>(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [tags, setTags] = useState('');
  const [agentRuntime, setAgentRuntime] = useState<AgentRuntime>('native');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [hosted, setHosted] = useState(false);
  const [hostingInitialized, setHostingInitialized] = useState(false);
  const [region, setRegion] = useState('eu-west-2');
  // server
  const [mode, setMode] = useState<ConnectionMode>('daytona');
  const [modeInitialized, setModeInitialized] = useState(false);
  const [sshHost, setSshHost] = useState('');
  const [workdirEdit, setWorkdir] = useState<string | null>(null);
  const [pair, setPair] = useState<string | null>(null);
  const [mfa, setMfa] = useState(false);
  // integrations
  const [picks, setPicks] = useState<Record<string, RepoPick>>({});
  const [jira, setJira] = useState<JiraDraft>(jiraDraft(null));
  // model & template
  const [modelId, setModelId] = useState<string | null>(null);
  // Library agents to start with (null until the library loads: then its defaults are pre-selected).
  const [picked, setPicked] = useState<string[] | null>(null);

  const pid = project?.id;
  const slug = project?.slug ?? slugify(name || 'new-project');
  const server = useServer(pid);
  const environment = useEnvironment(pid);
  const hosting = useQuery({ queryKey: ['hosting-config'], queryFn: () => api.get<{ enabled: boolean; blockedReason: string | null; defaultRegion: string; maxProjectsPerUser: number; instanceSize: string; monthlyComputeEstimateUsd: number | null; supabaseMode: 'dedicated' | 'shared' }>('/api/hosting/config') });
  useEffect(() => {
    if (!hosting.data || hostingInitialized) return;
    setHosted(hosting.data.enabled);
    setRegion(hosting.data.defaultRegion);
    setHostingInitialized(true);
  }, [hosting.data, hostingInitialized]);
  const conn = server.data?.connection ?? null;
  useEffect(() => {
    if (!server.data || modeInitialized) return;
    setMode(conn?.mode ?? (server.data.daytonaAvailable ? 'daytona' : 'local'));
    setModeInitialized(true);
  }, [server.data, conn, modeInitialized]);
  const integrations = useIntegrations(step === 2 ? pid : undefined);
  const repos = useGithubRepos(pid ?? '', step === 2 && !!integrations.data?.github.connected);
  const { data: models = [] } = useModels();
  const chatModels = models.filter((m) => m.capabilities.kind === 'chat');
  const library = useQuery({ queryKey: ['agent-library'], queryFn: () => api.get<AgentTemplateDTO[]>('/api/agent-library') });
  const chosen = picked ?? (library.data ?? []).filter((t) => t.includeByDefault).map((t) => t.id);
  const toggle = (id: string) => setPicked(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);

  useEffect(() => {
    if (!modelId && chatModels[0]) setModelId((chatModels.find((m) => !m.selfHosted) ?? chatModels[0]).id);
  }, [chatModels, modelId]);

  // Runner: poll until the daemon dials in.
  useEffect(() => {
    if (step !== 1 || mode !== 'runner' || !pid || conn?.status === 'online') return;
    const t = setInterval(() => void qc.invalidateQueries({ queryKey: qk.server(pid) }), 3000);
    return () => clearInterval(t);
  }, [step, mode, pid, conn?.status, qc]);

  // Pre-select repos returned by the token once.
  useEffect(() => {
    if (!repos.data) return;
    setPicks((p) => {
      const next = { ...p };
      for (const r of repos.data.slice(0, 20)) if (!next[r.fullName]) next[r.fullName] = { on: false, owner: r.owner, repo: r.repo, path: `repos/${r.repo}`, base: r.defaultBranch, role: '' };
      return next;
    });
  }, [repos.data]);

  const localRoot = server.data?.localRoot;
  // Default path follows the mode (and the server's local root once loaded) until the user edits it.
  const workdir = workdirEdit ?? (mode === 'daytona' ? '/home/daytona/workspace' : mode === 'local' ? (localRoot ? `${localRoot}/${slug}` : '') : `/srv/${slug}`);

  const refreshProjects = () => qc.invalidateQueries({ queryKey: qk.projects });

  const putServer = async (): Promise<ServerConnectionDTO | null> => {
    if (!pid) return null;
    setMfa(false);
    try {
      const r = await api.put<{ connection: ServerConnectionDTO; lastError: string | null }>(`/api/projects/${pid}/server`, {
        mode,
        host: mode === 'ssh' ? sshHost : undefined,
        workingDir: workdir || undefined,
      });
      await qc.invalidateQueries({ queryKey: qk.server(pid) });
      if (r.lastError) toast(r.lastError, 'error');
      return r.connection;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
      else toast(errorText(e), 'error');
      return null;
    }
  };

  const issuePair = async () => {
    if (!pid) return;
    setMfa(false);
    try {
      const r = await api.post<{ token: string }>(`/api/projects/${pid}/server/pair`);
      setPair(r.token);
      await qc.invalidateQueries({ queryKey: qk.server(pid) });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
      else toast(errorText(e), 'error');
    }
  };

  const pickMode = (m: ConnectionMode) => {
    setMode(m);
    setWorkdir(null);
    if (m === 'runner' && !pair) void issuePair();
  };

  const checkConn = async () => {
    if (!pid) return;
    setBusy(true);
    try {
      const r = await api.post<{ lastError: string | null }>(`/api/projects/${pid}/server/check`);
      await qc.invalidateQueries({ queryKey: qk.server(pid) });
      toast(r.lastError ? r.lastError : 'Connected', r.lastError ? 'error' : 'ok');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  const tagList = () =>
    tags
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

  const next = async () => {
    setErr('');
    if (step === 0) {
      if (!name.trim()) return setErr('Give the project a name to continue.');
      setBusy(true);
      try {
        const body = { name: name.trim(), description: desc.trim(), tags: tagList(), agentRuntime };
        const p = project ? await api.patch<ProjectDTO>(`/api/projects/${project.id}`, body) : await api.post<ProjectDTO>('/api/projects', { ...body, template: 'blank', hosting: { enabled: hosted, region } });
        setProject(p);
        await refreshProjects();
        setStep(1);
      } catch (e) {
        if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
        setErr(errorText(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === 1) {
      if (hosted) {
        if (environment.data?.environment?.state !== 'ready') return setErr('Wait for the Supabase and Daytona environment to be ready.');
        setStep(2);
        return;
      }
      // Connect on Continue if the chosen mode isn't set up yet.
      if (mode !== 'runner' && (!conn || conn.mode !== mode)) {
        setBusy(true);
        const c = await putServer();
        setBusy(false);
        if (!c) return;
        if (mode === 'ssh' && c.status !== 'online') return; // show the key so the user can install it
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      setBusy(true);
      try {
        const existing = new Set(integrations.data?.repos.map((r) => `${r.owner}/${r.repo}`));
        for (const [full, r] of Object.entries(picks)) {
          if (!r.on || existing.has(full)) continue;
          await api.post(`/api/projects/${pid}/repos`, { owner: r.owner, repo: r.repo, baseBranch: r.base || 'main', localPath: r.path || undefined, roleLabel: r.role });
        }
        if (jiraReady(jira, integrations.data?.jira?.hasToken)) await api.put(`/api/projects/${pid}/jira`, jiraBody(jira));
        await qc.invalidateQueries({ queryKey: qk.integrations(pid!) });
        setStep(3);
      } catch (e) {
        toast(errorText(e), 'error');
      } finally {
        setBusy(false);
      }
      return;
    }
    if (step === 3) return setStep(4);
    // Create: the chosen library agents, each on its preferred model when you have it, else the chosen one.
    if (!chosen.length) return toast('Pick at least one agent.', 'error');
    setBusy(true);
    try {
      const p = await api.post<ProjectDTO>(`/api/projects/${pid}/apply-template`, { agentTemplateIds: chosen, modelId });
      await refreshProjects();
      toast(`${p.name} created`);
      nav(`/p/${p.slug}`);
    } catch (e) {
      toast(errorText(e), 'error');
      setBusy(false);
    }
  };

  const origin = window.location.origin;
  const selectedRepos = useMemo(() => Object.values(picks).filter((r) => r.on).length, [picks]);

  return (
    <div className="screen">
      <TopBar
        settings={false}
        avatar={false}
        right={
          <Link className="btn btn-secondary" to="/">
            <X className="icon" />
            Cancel
          </Link>
        }
      />
      <div className="screen-body wizard-scroll">
    <div className="wizard">
      <aside className="wizard-steps card">
        <div>
          <div className="micro" style={{ fontSize: 11 }}>
            Step {step + 1} of {STEPS.length}
          </div>
          <h2 style={{ fontSize: 26, marginTop: 2 }}>New project</h2>
        </div>
        <ol>
          {STEPS.map((label, i) => (
            <li key={label}>
              <button className="wizard-step" disabled={i > 0 && !project} onClick={() => (i === 0 || project) && setStep(i)} aria-current={i === step ? 'step' : undefined}>
                <span className={`step-mark ${i < step ? 'done' : i === step ? 'cur' : ''}`}>{i < step ? '✓' : i + 1}</span>
                <span style={{ fontWeight: i === step ? 800 : 700 }}>{label}</span>
              </button>
            </li>
          ))}
        </ol>
      </aside>
      <main className="wizard-main card">
        <div className="wizard-body">

          {step === 0 && (
            <>
              <h1>Name the project</h1>
              <div className="field">
                <label htmlFor="w-name">Name</label>
                <input id="w-name" className="input" autoFocus value={name} onChange={(e) => (setName(e.target.value), setErr(''))} placeholder="e.g. Ledgerline" onKeyDown={(e) => e.key === 'Enter' && void next()} />
              </div>
              <div className="field">
                <span className="label">URL</span>
                <div className="mono muted slug-preview">
                  {window.location.host}/p/<b style={{ color: 'var(--color-text)' }}>{project?.slug ?? slugify(name || 'new-project')}</b>
                </div>
              </div>
              <div className="field">
                <label htmlFor="w-desc">Description</label>
                <textarea id="w-desc" className="input" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What the project is, in one line" />
              </div>
              <div className="field">
                <label htmlFor="w-tags">Tags</label>
                <input id="w-tags" className="input" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="comma separated" />
              </div>
              <div className="field">
                <label htmlFor="w-agent-runtime">AI workflow</label>
                <select id="w-agent-runtime" className="input" value={agentRuntime} onChange={(e) => setAgentRuntime(e.target.value as AgentRuntime)}>
                  <option value="native">None (built-in harness)</option>
                  <option value="pi">Pi</option>
                  <option value="opencode">OpenCode</option>
                </select>
                <div className="hint">Pi and OpenCode run their full agent workflow in the project workspace. They need npm and an Anthropic, OpenAI or Gemini API key. The provider key is passed to that workspace for each turn. Agent tool denies still apply; per-tool “Ask” approval cards are unavailable.</div>
              </div>
              {hosting.data?.enabled && !project && (
                <div className="tile tile-cream">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800 }}>
                    <input type="checkbox" checked={hosted} onChange={(e) => setHosted(e.target.checked)} />
                    Host this project on Supabase and Daytona
                  </label>
                  {hosted && hosting.data.supabaseMode === 'dedicated' && <div className="field" style={{ marginTop: 12 }}><label htmlFor="w-region">Supabase region</label><input id="w-region" className="input mono" value={region} onChange={(e) => setRegion(e.target.value)} /></div>}
                  <div className="hint">{hosting.data.supabaseMode === 'shared'
                    ? 'Creates a private database schema and login in the existing Supabase project, plus a Daytona sandbox. This adds no new Supabase project compute charge; Daytona usage and database usage still apply. Apps access the database through server-side code.'
                    : `Creates a dedicated Supabase database and a Daytona sandbox. Estimated Supabase ${hosting.data.instanceSize} compute: ${hosting.data.monthlyComputeEstimateUsd == null ? 'see Supabase pricing' : `about $${hosting.data.monthlyComputeEstimateUsd}/month`} while running, plus variable Daytona usage and other Supabase usage.`} Limit: {hosting.data.maxProjectsPerUser} hosted projects per owner.</div>
                </div>
              )}
              {hosting.data?.blockedReason && !project && <div className="tile tile-cream"><b>Supabase + Daytona hosting is unavailable</b><p className="muted">{hosting.data.blockedReason}</p></div>}
              {mfa && <MfaRequired />}
              {err && <div className="err">{err}</div>}
            </>
          )}

          {step === 1 && (
            <>
              <h1>{hosted ? 'Prepare your project environment' : 'Connect a server'}</h1>
              {hosted && (
                <div className="tile tile-cream">
                  <b>Supabase database + Daytona workspace</b>
                  <p className="muted">Provisioning runs in the background. You can watch each step here.</p>
                  <div className="status-line">{environment.data?.environment?.state === 'ready' ? '✓ Ready' : <><Spinner /> {environment.data?.environment?.state?.replaceAll('_', ' ') ?? 'Starting…'}</>}</div>
                  {environment.data?.environment?.errorDetail && <div className="err">{environment.data.environment.errorDetail}</div>}
                  <ol>{environment.data?.events.map((event, i) => <li key={i}>{event.outcome === 'error' ? '⚠' : '✓'} {event.step.replaceAll('_', ' ')}{event.detail ? ` · ${event.detail}` : ''}</li>)}</ol>
                  {environment.data?.environment?.state === 'failed' && <button className="btn btn-secondary" onClick={() => void api.post(`/api/projects/${pid}/environment/retry`).then(() => environment.refetch()).catch((e) => toast(errorText(e), 'error'))}>Retry provisioning</button>}
                </div>
              )}
              {!hosted && <>
              <p className="muted">The agent reads, edits and runs code on this server. Two-step verification must be on for your account first.</p>
              <div className="ruled-grid mode-grid">
                {MODES.filter((m) => server.data?.daytonaAvailable && !conn ? m.value === 'daytona' : conn?.mode === 'daytona' ? m.value === 'daytona' : m.value !== 'daytona').map((m) => (
                  <button key={m.value} className="pick-card" style={{ minHeight: 120 }} aria-pressed={mode === m.value} onClick={() => pickMode(m.value)}>
                    <span className="t">{m.label}</span>
                    <span className="d">{m.desc}</span>
                  </button>
                ))}
              </div>
              {mfa && <MfaRequired />}
              {mode === 'daytona' && (
                <div className="tile tile-cream">Continue to create a persistent Daytona workspace for this project. Its filesystem survives auto-stop, and usage is billed to your Daytona account.</div>
              )}
              {mode === 'runner' && (
                <>
                  <div className="field">
                    <span className="label">Run this on the server (one-time token, expires in 15 min)</span>
                    <pre className="code-dark">{pair ? `curl -fsSL ${origin}/install.sh | sh -s -- \\\n  --pair ${pair} --workdir /srv/${slug}` : 'Issuing a pairing token…'}</pre>
                  </div>
                  {conn?.mode === 'runner' && conn.status === 'online' ? (
                    <div className="success-banner sm">
                      <span className="success-icon sm">
                        <Check className="icon" />
                      </span>
                      <span style={{ flex: 1 }}>
                        <b className="title-900">Connected</b> · runner {conn.runnerVersion ?? ''} on {conn.host} · outbound WebSocket
                      </span>
                    </div>
                  ) : (
                    <div className="tile tile-cream status-line" style={{ fontWeight: 700 }}>
                      <Spinner />
                      <span style={{ flex: 1 }}>Waiting for the runner to dial in…</span>
                    </div>
                  )}
                  <div className="hint">Run it as a dedicated low-privilege user (e.g. <code>useradd --system harness</code>). The runner dials out over a WebSocket, so no inbound ports are opened.</div>
                </>
              )}
              {mode === 'ssh' && (
                <>
                  <div className="field">
                    <label htmlFor="w-host">Host</label>
                    <input id="w-host" className="input mono" value={sshHost} onChange={(e) => setSshHost(e.target.value)} placeholder="deploy@10.0.4.21" />
                  </div>
                  <div className="field">
                    <label htmlFor="w-wd">Working directory</label>
                    <input id="w-wd" className="input mono" value={workdir} onChange={(e) => setWorkdir(e.target.value)} />
                  </div>
                  {conn?.mode === 'ssh' && conn.publicKey ? (
                    <>
                      <div className="field">
                        <span className="label">Add this key to ~/.ssh/authorized_keys</span>
                        <pre className="code-light">{conn.publicKey}</pre>
                      </div>
                      <div className="row">
                        <button className="btn btn-secondary" onClick={() => void navigator.clipboard.writeText(conn.publicKey!).then(() => toast('Copied'))}>
                          <Copy className="icon" />
                          Copy key
                        </button>
                        <button className="btn btn-secondary" onClick={checkConn} disabled={busy}>
                          {busy && <Spinner />}Check connection
                        </button>
                        <span className={conn.status === 'online' ? '' : 'muted'} style={{ fontSize: 13 }}>
                          {conn.status === 'online' ? '✓ Connected' : conn.status === 'unreachable' ? (server.data?.lastError ?? 'Not reachable yet') : 'Not checked yet'}
                        </span>
                      </div>
                    </>
                  ) : (
                    <button className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => void putServer()} disabled={!sshHost.includes('@')}>
                      Generate key pair
                    </button>
                  )}
                </>
              )}
              {mode === 'local' && (
                <div className="field">
                  <label htmlFor="w-root">Project root on this host</label>
                  <input id="w-root" className="input mono" value={workdir} onChange={(e) => setWorkdir(e.target.value)} />
                  <div className="hint">
                    Must be under {localRoot ?? 'the configured projects root'}. {conn?.mode === 'local' && conn.status === 'online' ? '✓ Connected' : 'Connects when you continue.'}
                  </div>
                </div>
              )}
              </>}
            </>
          )}

          {step === 2 && (
            <>
              <h1>Link GitHub and Jira</h1>
              {integrations.error ? (
                <div className="err">Integrations aren’t available on this server yet — skip this step.</div>
              ) : (
                <>
                  <GithubConnect projectId={pid!} info={integrations.data?.github} onChange={() => qc.invalidateQueries({ queryKey: qk.integrations(pid!) })} />
                  {integrations.data?.github.connected && (
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }} />
                          <th>Repository</th>
                          <th>Path on server</th>
                          <th style={{ width: 110 }}>Base</th>
                          <th style={{ width: 120 }}>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repos.isLoading && (
                          <tr>
                            <td colSpan={5}>
                              <Spinner />
                            </td>
                          </tr>
                        )}
                        {Object.entries(picks).map(([full, r]) => {
                          const set = (p: Partial<RepoPick>) => setPicks({ ...picks, [full]: { ...r, ...p } });
                          return (
                            <tr key={full}>
                              <td style={{ cursor: 'pointer' }} onClick={() => set({ on: !r.on })}>
                                <button className="btn-reset" aria-label={`Link ${full}`} aria-pressed={r.on} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}>
                                  <Check2 on={r.on} />
                                </button>
                              </td>
                              <td style={{ fontWeight: 600, cursor: 'pointer' }} onClick={() => set({ on: !r.on })}>
                                {full}
                              </td>
                              <td>
                                <input className="input mono" style={{ minHeight: 28, padding: '2px 8px' }} aria-label="Path" value={r.path} onChange={(e) => set({ path: e.target.value })} />
                              </td>
                              <td>
                                <input className="input" style={{ minHeight: 28, padding: '2px 8px' }} aria-label="Base branch" value={r.base} onChange={(e) => set({ base: e.target.value })} />
                              </td>
                              <td>
                                <input className="input" style={{ minHeight: 28, padding: '2px 8px' }} aria-label="Role" placeholder="backend" value={r.role} onChange={(e) => set({ role: e.target.value })} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                  <div className="rule" />
                  <JiraFields value={jira} onChange={setJira} projectId={pid} />
                  {!integrations.data?.github.connected && (
                    <div className="hint" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <GithubIcon /> You can link repos and Jira later in Settings → GitHub & Jira.
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <h1>Pick the default model</h1>
              <p className="muted">Used by the default agent. Each agent can override it later.</p>
              {chatModels.length === 0 ? (
                <div className="status-line">
                  <span style={{ flex: 1 }}>No models yet. Add a provider (Anthropic, OpenAI or a self-hosted endpoint), then come back.</span>
                  <a className="btn btn-secondary" href="/settings/providers" target="_blank" rel="noreferrer">
                    Add a provider
                  </a>
                  <button className="btn btn-ghost" onClick={() => qc.invalidateQueries({ queryKey: qk.models })}>
                    Refresh
                  </button>
                </div>
              ) : (
                <div className="model-list" role="radiogroup" aria-label="Default model">
                  {chatModels.map((m) => (
                    <button key={m.id} role="radio" aria-checked={modelId === m.id} className="model-radio" onClick={() => setModelId(m.id)}>
                      <span className={`radio ${modelId === m.id ? 'on' : ''}`} />
                      <span>
                        <span style={{ display: 'block', font: '800 14px var(--font-heading)' }}>{m.modelName}</span>
                        <span className="muted" style={{ fontSize: 12 }}>
                          {m.providerName}
                          {m.selfHosted ? ' (self-hosted)' : ''}
                        </span>
                      </span>
                      <span className="tag tag-neutral">{formatTokens(m.contextWindow)} context</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {step === 4 && (
            <>
              <h1>Choose your agents</h1>
              <p className="muted" style={{ marginTop: -6 }}>From your organisation's agent library. You can add, remove or tune agents later in the project's Agents settings.</p>
              <div className="ruled-grid tpl-grid">
                {(library.data ?? []).filter((t) => !MODE_ROLES.includes(t.role)).map((t) => {
                  const own = t.preferredModel && models.some((m) => m.modelName === t.preferredModel);
                  return (
                    <button key={t.id} className="pick-card" aria-pressed={chosen.includes(t.id)} onClick={() => toggle(t.id)}>
                      <span className="t">
                        {t.name}
                        {t.isDefaultAgent ? ' · default' : ''}
                      </span>
                      <span className="d">{t.description || t.role}</span>
                      <span className="d" style={{ opacity: 0.65 }}>
                        {t.role} · {own ? t.preferredModel : (chatModels.find((m) => m.id === modelId)?.modelName ?? 'your model')}
                      </span>
                    </button>
                  );
                })}
                {library.isLoading && <Spinner />}
              </div>
              <div className="muted" style={{ fontSize: 13 }}>
                {name} · {conn ? `${MODES.find((m) => m.value === conn.mode)?.label} (${conn.status})` : 'no server yet'} · {selectedRepos || integrations.data?.repos.length || 0} repos · {chatModels.find((m) => m.id === modelId)?.modelName ?? 'no model'}
              </div>
            </>
          )}

          <div className="wizard-foot">
            {step > 0 && (
              <button className="btn btn-secondary" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="icon" />
                Back
              </button>
            )}
            <span style={{ flex: 1 }} />
            {((step === 1 && !hosted) || step === 2) && (
              <button className="btn btn-ghost" onClick={() => setStep(step + 1)}>
                Skip for now
              </button>
            )}
            <button className="btn btn-primary btn-lg" onClick={next} disabled={busy || (step === 1 && hosted && environment.data?.environment?.state !== 'ready')}>
              {step === 4 ? 'Create project' : 'Continue'}
              {busy ? <Spinner /> : <ArrowRight className="icon" />}
            </button>
          </div>
        </div>
      </main>
    </div>
      </div>
    </div>
  );
}
